import { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "pdm-cookie-consent";
const CONSENT_VERSION = 1;
// 13 mois (recommandation CNIL), en millisecondes
const MAX_AGE_MS = 13 * 30 * 24 * 60 * 60 * 1000;

const ConsentContext = createContext(null);

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    if (parsed.date && Date.now() - new Date(parsed.date).getTime() > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const loadAnalytics = () => {
  const gaId = process.env.REACT_APP_GA_ID;
  if (!gaId || window.__pdmGaLoaded) return;
  window.__pdmGaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", gaId, { anonymize_ip: true });
};

export function ConsentProvider({ children }) {
  const [consent, setConsentState] = useState(() => readConsent());
  const [showManager, setShowManager] = useState(false);

  const persist = useCallback((categories) => {
    const record = {
      version: CONSENT_VERSION,
      date: new Date().toISOString(),
      categories: { necessary: true, ...categories },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setConsentState(record);
    setShowManager(false);
  }, []);

  const acceptAll = useCallback(() => persist({ analytics: true, social: true }), [persist]);
  const rejectAll = useCallback(() => persist({ analytics: false, social: false }), [persist]);
  const savePreferences = useCallback((prefs) => persist(prefs), [persist]);

  // Charge les scripts non essentiels uniquement si consentement donné
  useEffect(() => {
    if (consent?.categories?.analytics) loadAnalytics();
  }, [consent]);

  const value = {
    consent,
    hasChoice: !!consent,
    categories: consent?.categories || { necessary: true, analytics: false, social: false },
    showManager,
    openManager: () => setShowManager(true),
    closeManager: () => setShowManager(false),
    acceptAll,
    rejectAll,
    savePreferences,
  };

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent doit être utilisé dans un ConsentProvider");
  return ctx;
};
